import axios from 'axios';
import Cookies from 'js-cookie';
import { User } from '../contexts/AuthContext';
import config from '../config/environment';
import { safeRedirect, isValidRedirectUrl } from '../utils/securityUtils';

// OAuth2 Configuration for Authorization Code flow (with client secret)
// Now uses dynamic environment-based configuration
const OAUTH_CONFIG = {
  authServerUrl: config.authServerUrl,
  clientId: config.clientId,
  clientSecret: config.clientSecret,
  redirectUri: config.redirectUri,
  scope: config.scope,
  responseType: 'code',
};

// Log OAuth configuration in non-production environments
if (!config.isProduction) {
  console.log('🔐 OAuth Configuration Loaded:', {
    environment: config.environment,
    authServerUrl: OAUTH_CONFIG.authServerUrl,
    clientId: OAUTH_CONFIG.clientId,
    redirectUri: OAUTH_CONFIG.redirectUri,
    scope: OAUTH_CONFIG.scope,
  });
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: OAUTH_CONFIG.authServerUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Axios interceptor to include access token in requests
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await refreshAccessToken();
        const token = getAccessToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        logout();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Utility functions
function generateRandomState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(digest));
  const base64Digest = btoa(String.fromCharCode.apply(null, hashArray));
  return base64Digest.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Token management
function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

function setAccessToken(token: string): void {
  localStorage.setItem('access_token', token);
  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('authTokenChanged', { detail: { token } }));
}

function getRefreshToken(): string | null {
  return Cookies.get('refresh_token') || null;
}

function setRefreshToken(token: string): void {
  // Browser detection for cookie settings
  const userAgent = navigator.userAgent;
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  
  // Safari-specific cookie settings
  const cookieOptions = {
    expires: 7, // 7 days
    secure: window.location.protocol === 'https:',
    sameSite: isSafari ? 'none' as const : 'lax' as const, // Safari needs 'none' for cross-site
  };
  
  console.log('🍪 Setting refresh token cookie with options:', cookieOptions);
  
  // Set secure cookie for refresh token
  Cookies.set('refresh_token', token, cookieOptions);
}

// ID Token management
function getIdToken(): string | null {
  return localStorage.getItem('id_token');
}

function setIdToken(token: string): void {
  localStorage.setItem('id_token', token);
}

function clearTokens(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  Cookies.remove('refresh_token');
  localStorage.removeItem('code_verifier');
  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('authTokenChanged', { detail: { token: null } }));
}

// OAuth2 Auth Service
export const authService = {
  // Check if current URL indicates Chrome DevTools interference
  checkChromeDevToolsInterference: (): boolean => {
    const url = window.location.href;
    console.log('🔍 Checking for Chrome DevTools interference. Current URL:', url);
    const isInterference = url.includes('.well-known/appspecific/com.chrome.devtools.json') || 
           url.includes('/error?continue');
    console.log('🔍 Chrome DevTools interference detected:', isInterference);
    return isInterference;
  },

  // Recover from Chrome DevTools interference
  recoverFromChromeInterference: (): void => {
    console.log('🟡 Attempting to recover from Chrome DevTools interference...');
    const urlParams = new URLSearchParams(window.location.search);
    const continueUrl = urlParams.get('continue');
    
    console.log('🔍 Current URL params:', window.location.search);
    console.log('🔍 Continue URL found:', continueUrl);
    
    if (continueUrl) {
      console.log('🟡 Recovering from Chrome DevTools interference, redirecting to:', continueUrl);
      safeRedirect(continueUrl, '/');
      return;
    }
    
    // If no continue URL, redirect to home and let user try again
    console.log('🟡 Chrome DevTools interference detected, redirecting to home');
    safeRedirect('/', '/');
  },
  // Initiate OAuth2 login flow - redirects to Spring Boot login page
  login: (redirectUri?: string) => {
    try {
      // Browser detection for debugging
      const userAgent = navigator.userAgent;
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/Chromium/.test(userAgent);
      const isChrome = /Chrome/.test(userAgent) || /Chromium/.test(userAgent);
      const isFirefox = /Firefox/.test(userAgent);
      
      console.log('🚀 Login button clicked - starting OAuth2 flow');
      console.log('🌐 User Agent:', userAgent);
      console.log('🌐 Browser Detection:', { isSafari, isChrome, isFirefox });
      
      // Check for Chrome DevTools interference
      if (isChrome && authService.checkChromeDevToolsInterference()) {
        console.log('🟡 Chrome DevTools interference detected during login attempt');
        authService.recoverFromChromeInterference();
        return;
      }
      
      // Store the intended redirect URI for after login
      if (redirectUri) {
        localStorage.setItem('post_login_redirect', redirectUri);
      } else {
        // Store current location as fallback
        localStorage.setItem('post_login_redirect', window.location.pathname);
      }
      
      // Generate and store state for security
      const state = generateRandomState();
      localStorage.setItem('oauth_state', state);
      
      // Build OAuth2 authorization URL - Spring Boot will handle login UI
      const params = new URLSearchParams({
        response_type: OAUTH_CONFIG.responseType,
        client_id: OAUTH_CONFIG.clientId,
        redirect_uri: OAUTH_CONFIG.redirectUri,
        scope: OAUTH_CONFIG.scope,
        state: state,
      });
      
      const authUrl = `${OAUTH_CONFIG.authServerUrl}/oauth2/authorize?${params.toString()}`;
      console.log('=== OAuth2 Login Flow ===');
      console.log('Environment detected:', config.environment);
      console.log('Redirecting to OAuth2 authorization URL:', authUrl);
      console.log('State parameter:', state);
      console.log('Client ID:', OAUTH_CONFIG.clientId);
      console.log('Configured Redirect URI:', OAUTH_CONFIG.redirectUri);
      console.log('Current window origin:', window.location.origin);
      console.log('Scope:', OAUTH_CONFIG.scope);
      console.log('Auth server base URL:', OAUTH_CONFIG.authServerUrl);
      
      // Add Chrome-specific warning
      if (isChrome) {
        console.log('🟡 Chrome detected - may have DevTools interference issues');
      }
      
      // Add a small delay to ensure console logs are visible
      setTimeout(() => {
        console.log('🔄 Redirecting to Spring Boot OAuth2 server...');
        // Redirect directly to Spring Boot authorization endpoint
        // Spring Boot will show its own login page
        // Allow OAuth server URL as it's a trusted authentication endpoint
        safeRedirect(authUrl, '/', [window.location.origin, OAUTH_CONFIG.authServerUrl]);
      }, 100);
      
    } catch (error) {
      console.error('❌ Error in login function:', error);
      throw new Error('Failed to initiate login');
    }
  },

  // Handle OAuth2 callback from Spring Boot authorization server
  handleCallback: async (code: string, state?: string): Promise<User> => {
    try {
      console.log('=== OAUTH CALLBACK DEBUG ===');
      console.log('Starting callback handling with code:', code.substring(0, 10) + '...');
      console.log('State parameter:', state);
      console.log('Current URL:', window.location.href);
      console.log('Token endpoint URL:', `${OAUTH_CONFIG.authServerUrl}/oauth2/token`);
      console.log('Configured redirect URI:', OAUTH_CONFIG.redirectUri);
      console.log('Current origin:', window.location.origin);
      
      // Verify state parameter for security
      const storedState = localStorage.getItem('oauth_state');
      console.log('Stored state:', storedState);
      console.log('Received state:', state);
      
      if (state && storedState && state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }
      
      // Clear the stored state
      localStorage.removeItem('oauth_state');
      
      // Exchange authorization code for tokens
      console.log('Exchanging code for tokens...');
      
      // For CLIENT_SECRET_BASIC, send credentials in Authorization header
      const tokenData = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: OAUTH_CONFIG.redirectUri,
      });
      
      // Create Basic Auth header for client authentication
      const clientCredentials = btoa(`${OAUTH_CONFIG.clientId}:${OAUTH_CONFIG.clientSecret}`);
      
      console.log('=== TOKEN REQUEST DEBUG ===');
      console.log('Token request data:', {
        grant_type: 'authorization_code',
        code: code.substring(0, 10) + '...',
        redirect_uri: OAUTH_CONFIG.redirectUri,
        clientAuth: 'Basic Auth header (CLIENT_SECRET_BASIC)',
        authHeader: `Basic ${clientCredentials.substring(0, 20)}...`,
        clientId: OAUTH_CONFIG.clientId,
        clientSecret: OAUTH_CONFIG.clientSecret.substring(0, 3) + '***'
      });

      console.log('Full request details:', {
        url: `${OAUTH_CONFIG.authServerUrl}/oauth2/token`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${clientCredentials.substring(0, 20)}...`,
        },
        body: tokenData.toString()
      });

      // Use a separate axios instance for token exchange to avoid interceptors
      const tokenResponse = await axios.post(
        `${OAUTH_CONFIG.authServerUrl}/oauth2/token`,
        tokenData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${clientCredentials}`,
          },
          withCredentials: true,
          timeout: 10000, // 10 second timeout
        }
      );

      console.log('=== TOKEN RESPONSE SUCCESS ===');
      console.log('Token exchange response:', {
        status: tokenResponse.status,
        statusText: tokenResponse.statusText,
        hasAccessToken: !!tokenResponse.data.access_token,
        hasRefreshToken: !!tokenResponse.data.refresh_token,
        hasIdToken: !!tokenResponse.data.id_token,
        tokenType: tokenResponse.data.token_type,
        expiresIn: tokenResponse.data.expires_in,
        allTokens: Object.keys(tokenResponse.data)
      });

      const { access_token, refresh_token, id_token, token_type } = tokenResponse.data;
      
      if (!access_token) {
        console.error('Token response data:', tokenResponse.data);
        throw new Error('No access token received from authorization server');
      }

      console.log('Storing tokens in localStorage...');
      setAccessToken(access_token);
      if (refresh_token) {
        console.log('Storing refresh token...');
        setRefreshToken(refresh_token);
      } else {
        console.log('⚠️  No refresh token received in response');
      }
      if (id_token) {
        console.log('Storing ID token...');
        setIdToken(id_token);
      } else {
        console.log('⚠️  No ID token received in response');
      }
      
      // Verify tokens were stored
      const storedAccessToken = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      const storedIdToken = localStorage.getItem('id_token');
      console.log('Tokens stored verification:', {
        accessTokenStored: !!storedAccessToken,
        refreshTokenStored: !!storedRefreshToken,
        idTokenStored: !!storedIdToken
      });
      
      console.log('Tokens stored successfully, fetching user info...');
      
      // Get user info using the access token
      const user = await authService.getCurrentUser();
      console.log('User info retrieved successfully:', user);
      
      return user;
    } catch (error) {
      console.error('=== TOKEN EXCHANGE ERROR ===');
      console.error('Error handling callback:', error);
      
      // Provide more specific error messages
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error('Token exchange failed - Response details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers,
            url: error.config?.url,
            method: error.config?.method,
            requestData: error.config?.data
          });
          
          // Log specific OAuth error details
          if (error.response.data) {
            console.error('OAuth Error Details:', {
              error: error.response.data.error,
              errorDescription: error.response.data.error_description,
              errorUri: error.response.data.error_uri
            });
          }
          
          throw new Error(`Token exchange failed: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
        } else if (error.request) {
          console.error('Network error during token exchange:', error.request);
          throw new Error('Network error: Could not reach authorization server');
        }
      }
      
      throw new Error(`Authentication failed: ${(error as Error).message}`);
    }
  },

  // Get user UUID from JWT token
  getUserUuid: (): string => {
    try {
      const token = getAccessToken();
      if (!token || !token.includes('.')) {
        throw new Error('No valid JWT token found');
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const userUuid = payload.user_uuid || payload.sub || payload.user_id;
      
      if (!userUuid) {
        throw new Error('No user UUID found in token');
      }

      console.log('Extracted user UUID from token:', userUuid);
      return userUuid;
    } catch (error) {
      console.error('Error extracting user UUID from token:', error);
      throw new Error('Could not extract user UUID from authentication token');
    }
  },

  // Get current user info
  getCurrentUser: async (): Promise<User> => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error('No access token found');
      }

      console.log('Fetching user info with token:', token.substring(0, 20) + '...');

      // Prioritize JWT parsing since it has all the data we need
      if (token.includes('.')) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('Decoded JWT payload:', payload);
          console.log('Name fields in JWT:', {
            given_name: payload.given_name,
            family_name: payload.family_name,
            name: payload.name
          });
          console.log('Status fields in JWT:', {
            status: payload.status,
            provider_status: payload.provider_status,
            roles: payload.roles,
            authorities: payload.authorities
          });
          
          // JWT has the data, use it directly
          const rawRoles = payload.roles || payload.authorities || [];
          const rolesArray = Array.isArray(rawRoles) ? rawRoles : (rawRoles ? [rawRoles] : []);
          
          const user = {
            id: payload.sub || payload.user_id || 'unknown',
            username: payload.preferred_username || payload.username || payload.email || payload.sub,
            email: payload.email || '',
            firstName: payload.given_name || payload.first_name || payload.name?.split(' ')[0] || '',
            lastName: payload.family_name || payload.last_name || payload.name?.split(' ')[1] || '',
            roles: rolesArray,
            userUuid: payload.user_uuid || payload.sub || payload.user_id || 'unknown', // Add user_uuid field
            status: payload.status || payload.provider_status || 'ACTIVE', // Add status field extraction
          };
          
          console.log('User from JWT - Raw payload roles:', payload.roles);
          console.log('User from JWT - Raw payload authorities:', payload.authorities);
          console.log('User from JWT - Processed roles array:', rolesArray);
          console.log('User from JWT - Final user object:', user);
          console.log('User from JWT - User status:', user.status);
          console.log('User from JWT - Is roles array?', Array.isArray(user.roles));
          return user;
        } catch (jwtError) {
          console.error('Failed to decode JWT token, falling back to userinfo endpoint:', jwtError);
        }
      }

      // Fallback: Try the OIDC userinfo endpoint if JWT parsing fails
      let response;
      try {
        response = await api.get('/userinfo');
        console.log('Userinfo response (fallback):', response.data);
      } catch (userInfoError) {
        console.error('Both JWT parsing and userinfo endpoint failed:', userInfoError);
        throw userInfoError;
      }

      const userInfo = response.data;
      
      console.log('Userinfo endpoint - Raw response:', userInfo);
      console.log('Userinfo endpoint - roles field:', userInfo.roles);
      console.log('Userinfo endpoint - authorities field:', userInfo.authorities);
      
      const rawRoles = userInfo.roles || userInfo.authorities || [];
      const rolesArray = Array.isArray(rawRoles) ? rawRoles : (rawRoles ? [rawRoles] : []);
      
      const finalUser = {
        id: userInfo.sub || userInfo.user_id || 'unknown',
        username: userInfo.preferred_username || userInfo.username || userInfo.email || userInfo.sub,
        email: userInfo.email || '',
        firstName: userInfo.given_name || userInfo.first_name || userInfo.name?.split(' ')[0] || '',
        lastName: userInfo.family_name || userInfo.last_name || userInfo.name?.split(' ')[1] || '',
        roles: rolesArray,
        userUuid: userInfo.user_uuid || userInfo.sub || userInfo.user_id || 'unknown', // Add user_uuid field
        status: userInfo.status || userInfo.provider_status || 'ACTIVE', // Add status field extraction
      };
      
      console.log('Userinfo endpoint - Processed roles array:', rolesArray);
      console.log('Userinfo endpoint - Final user:', finalUser);
      console.log('Userinfo endpoint - Is roles array?', Array.isArray(finalUser.roles));
      
      return finalUser;
    } catch (error) {
      console.error('Error getting current user:', error);
      
      if (axios.isAxiosError(error) && error.response) {
        console.error('User info endpoint error:', {
          status: error.response.status,
          data: error.response.data,
          url: error.config?.url
        });
        
        if (error.response.status === 401) {
          // Token might be invalid, clear it
          clearTokens();
          throw new Error('Authentication token is invalid or expired');
        }
      }
      
      throw new Error(`Failed to get user information: ${(error as Error).message}`);
    }
  },

  // Refresh access token
  refreshAccessToken: async (): Promise<void> => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token found');
      }

      // Create Basic Auth header for client authentication
      const clientCredentials = btoa(`${OAUTH_CONFIG.clientId}:${OAUTH_CONFIG.clientSecret}`);

      const response = await axios.post(
        `${OAUTH_CONFIG.authServerUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${clientCredentials}`,
          },
        }
      );

      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      setAccessToken(access_token);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      clearTokens();
      throw new Error('Failed to refresh token');
    }
  },

  // Logout with consistent behavior
  logout: async (): Promise<void> => {
    try {
      const token = getAccessToken();
      const idToken = getIdToken();
      
      console.log('=== LOGOUT INITIATED ===');
      console.log('Access token available:', !!token);
      console.log('ID token available:', !!idToken);
      
      // Always clear tokens locally first for immediate UI update
      clearTokens();
      console.log('🧹 Local tokens cleared');
      
      // Try to revoke token on server (best effort)
      if (token) {
        try {
          console.log('🔄 Attempting to revoke access token...');
          await Promise.race([
            api.post('/oauth2/revoke', {
              token: token,
              token_type_hint: 'access_token',
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Token revocation timeout')), 3000)
            )
          ]);
          console.log('✅ Access token revoked successfully');
        } catch (error) {
          console.warn('⚠️ Error revoking token on server (continuing logout):', error);
        }
      }
      
      // Perform OIDC logout with fallback (consistent redirect to home)
      try {
        // Use a timeout to prevent hanging logout process
        const logoutPromise = new Promise<void>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('OIDC logout timeout'));
          }, 5000);
          
          try {
            let logoutUrl = `${OAUTH_CONFIG.authServerUrl}/connect/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
            
            // Add id_token_hint if available (OIDC spec recommended)
            if (idToken) {
              logoutUrl += `&id_token_hint=${encodeURIComponent(idToken)}`;
              console.log('🔑 Including ID token hint in logout request');
            }

            console.log('🔄 Initiating OIDC logout with timeout protection');
            
            // Clear timeout and redirect
            clearTimeout(timeoutId);
            window.location.href = logoutUrl;
            resolve();
          } catch (error) {
            clearTimeout(timeoutId);
            reject(error);
          }
        });
        
        await logoutPromise;
      } catch (error) {
        console.warn('⚠️ OIDC logout failed, redirecting to home page:', error);
        // Always redirect to home page for consistent user experience
        window.location.href = window.location.origin;
      }
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Ensure tokens are cleared and redirect to home
      clearTokens();
      window.location.href = window.location.origin;
    }
  },

  // Alternative local-only logout (faster, no server dependency)
  logoutLocal: (): void => {
    clearTokens();
    safeRedirect(window.location.origin, '/');
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!getAccessToken();
  },

  // Get API instance for making authenticated requests
  getApiInstance: () => api,
};

// Export the refresh function for use in the interceptor
async function refreshAccessToken(): Promise<void> {
  return authService.refreshAccessToken();
}

// Export logout function for use in the interceptor
function logout(): void {
  clearTokens();
  safeRedirect('/', '/');
}